<template>
    <v-card :style="`${iosSafeArea ? 'padding-top: 45px!important; ' : ''}`">
        <v-progress-linear v-if="reloading" color="primary" indeterminate rounded absolute top height="3" ></v-progress-linear>

        <v-btn icon top right :large="phoneOnly" absolute style="top: 2px; right: 2px;" :style="`${iosSafeArea ? 'top: 45px!important; ' : ''}`" @click="$emit('close')">
            <v-icon :size="pcOnly ? 21 : 24">mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center py-4 px-5">
            <div class="me-4">
                <template v-if="isSession">
                    <v-avatar tile width="60" height="50" style="border-radius: 5px!important" :class="`${sessionColor} white--text`">
                        <v-icon dark>{{ isConsultation ? 'mdi-message-text-outline' : 'mdi-clipboard-check-outline' }}</v-icon>
                    </v-avatar>
                </template>
                <template v-else>
                    <router-link :to="isStudent ? {name: 'course', params: {id: classData.enrollment.course.id}} : ''">
                        <v-avatar tile width="60" height="50" style="border-radius: 5px!important" v-ripple="{class: 'black--text'}">
                            <v-img :src="classData.enrollment.course.image_url" />
                        </v-avatar>
                    </router-link>
                </template>
            </div>
            <div class="overflow-hidden">
                <template v-if="isSession">
                    <div class="strong-text--text font-weight-medium nowrap">{{ sessionLabel }} Session</div>
                </template>
                <template v-else>
                    <router-link :to="isStudent ? {name: 'course', params: {id: classData.enrollment.course.id}} : ''">
                        <div class="strong-text--text font-weight-medium nowrap">{{classData.name || classData.enrollment.course.name}}</div>
                    </router-link>
                </template>
                <div class="d-flex">
                    <v-chip v-if="classData.holiday_on_hold || classData.full_status === 'on_hold' || classData.status === 'on_hold'" small class="font-weight-medium" dark color="grey darken-2" label>
                        <v-icon small left class="me-1">mdi-calendar-remove</v-icon>
                        <span>On Hold</span>
                    </v-chip>
                    <v-chip v-else-if="classData.rescheduled && classData.status === 'scheduled'" small class="font-weight-medium" dark color="teal darken-1" label>
                        <v-icon small left class="me-1">mdi-calendar-clock-outline</v-icon>
                        <span v-if="!!classData.waiting_reschedule_request">Reschedule Requested</span>
                        <span v-else>Rescheduled</span>
                    </v-chip>
                    <v-chip v-else-if="!classData.absent && !classData.canceled && !classData.uncharged && !isSession" small class="font-weight-medium" dark :color="handleStatus(classData.full_status).color" label>{{handleStatus(classData.full_status).text}}</v-chip>

                    <v-chip v-if="classData.makeup" small class="font-weight-medium ms-1" dark color="deep-purple accent-4" label>
                        <v-icon size="17" left class="me-1">mdi-shape-square-rounded-plus</v-icon>
                        <span>Makeup</span>
                    </v-chip>

                    <v-chip v-if="classData.uncharged" small class="font-weight-medium ms-1" dark color="brown darken-2" label>
                        <v-icon small left class="me-1">mdi-cash-remove</v-icon>
                        Uncharged
                    </v-chip>

                    <v-chip v-if="classData.uncharged && classData.limit_exceeded" small class="font-weight-medium ms-1" dark color="red accent-1" label>
                        <v-icon small left class="me-1">mdi-alert-octagon-outline</v-icon>
                        Limit Reached
                    </v-chip>

                    <v-chip v-if="classData.absent" small class="font-weight-medium ms-1" dark color="amber darken-1" label>
                        <v-icon small left class="me-1">mdi-close-octagon-outline</v-icon>
                        Absent
                    </v-chip>
                    
                    <v-chip v-if="classData.canceled" small class="font-weight-medium ms-1" dark color="red darken-1" label>
                        <v-icon small left class="me-1">mdi-close</v-icon>
                        Canceled
                    </v-chip>

                    <div v-if="classData.payment_status || classData.is_unchargeable" class="d-flex ms-1">
                        <v-chip v-if="classData.payment_status === 'paid'" small class="font-weight-medium" dark label>
                            <v-icon size="20" left class="me-1">mdi-cash-check</v-icon>
                            <span>Paid</span>
                        </v-chip>
                        <v-chip v-else-if="classData.payment_status === 'evaluation'" small class="font-weight-medium" dark color="cyan darken-1" label>
                            <v-icon size="17" left class="me-1">mdi-clipboard-check-outline</v-icon>
                            <span>Evaluation</span>
                        </v-chip>
                        <v-chip v-else-if="classData.payment_status === 'consultation'" small class="font-weight-medium" dark color="lime darken-3" label>
                            <v-icon size="17" left class="me-1">mdi-message-text-outline</v-icon>
                            <span>Consultation</span>
                        </v-chip>
                        <v-chip v-else-if="classData.payment_status === 'trial'" small class="font-weight-medium" dark color="deep-purple" label>
                            <v-icon size="17" left class="me-1">mdi-check-decagram</v-icon>
                            <span>Trial</span>
                        </v-chip>
                        <v-chip v-else-if="classData.is_unchargeable && classData.status !== 'expired'" small class="font-weight-medium" dark color="pink darken-1" label>
                            <v-icon size="18" left class="me-1">mdi-alert-circle-outline</v-icon>
                            <span>No Balance</span>
                        </v-chip>
                        <v-chip v-else-if="classData.payment_status === 'credits-not-enough' && classData.status !== 'expired'" small class="font-weight-medium" dark color="red" label>
                            <v-icon size="18" left class="me-1">mdi-wallet</v-icon>
                            <span>Recharge Required</span>
                        </v-chip>
                    </div>
                </div>

            </div>
        </div>
        <v-divider class="mx-5 mb-3"></v-divider>
        <v-card-text class="d-flex flex-column px-1 pb-2">
            <div class="px-3">
                <v-alert v-if="isSoon && !classData.canceled" dense small text dark color="blue darken-1" class="px-3 mb-2">
                    <div class="details-text font-weight-medium strong-text--text">
                        <v-icon color="blue darken-2" size="18" class="me-1">mdi-timer-sand</v-icon>
                        Starts in: <b class="strong-text--text">{{countdownTime}}</b>
                    </div>
                </v-alert>
                <v-alert v-else-if="classData.status !== 'expired' && classData.status !== 'running'" dense small text color="grey" class="px-3 mb-2">
                    <div class="details-text font-weight-medium strong-text--text">
                        <v-icon color="grey darken-1" size="18" class="me-1">mdi-clock-outline</v-icon>
                        Times are in your local time <b>{{timeNow}}</b>
                    </div>
                </v-alert>

                <v-alert v-if="classData.canceled && classData.cancelation_reason" dense small text dark  color="error darken-1" class="px-3 mb-2">
                    <div class="details-text font-weight-medium strong-text--text e-scrollbar" style="max-height: 100px; overflow-y: auto;">
                        <v-icon color="error darken-2" size="18" class="me-1">mdi-close-circle-outline</v-icon>
                        <span class="error--text text--darken-2">cancelation reason:</span>
                        <br>
                        <span class="details-text font-weight-medium" style="white-space: pre-line;">{{classData.cancelation_reason}}</span>
                    </div>
                </v-alert>
            </div>

            <div v-if="classData.holiday_on_hold" class="mx-4">
                <v-alert dense small outlined text type="info" color="grey darken-2" class="px-3">
                    <div class="details-text font-weight-medium strong-text--text">
                        <v-icon color="grey darken-2" size="18" class="me-1">mdi-calendar-remove</v-icon>
                        <b>Class On Hold:</b>
                        <br>
                        This class is on hold due to an approved holiday request. You cannot join, reschedule, or cancel this class.
                    </div>
                </v-alert>
            </div>
            <div v-else-if="classData.is_unchargeable && classData.payment_status !== 'paid' && classData.status !== 'expired'" class="mx-4">
                <router-link v-if="isStudent" :to="{name: 'wallet'}" style="text-decoration: none">
                    <v-alert dense small outlined text type="warning" color="pink darken-1" class="e-link px-3" v-ripple>
                        <div class="details-text font-weight-medium">
                            <b>Insufficient Credits:</b>
                            <br>
                            You don't have enough credits to cover this class. Please recharge your wallet to ensure uninterrupted learning.
                            <br>
                            <v-btn small class="mt-2" color="primary" dark depressed style="text-transform: none" :to="{name: 'wallet'}" @mousedown.stop @touchstart.stop @click.stop>
                                <v-icon left size="17">mdi-wallet-plus-outline</v-icon>
                                Recharge Now
                            </v-btn>
                        </div>
                    </v-alert>
                </router-link>
                <v-alert v-else outlined dense small text type="warning" color="pink darken-1" class="px-3">
                    <div class="details-text font-weight-medium">
                        <b>Student's credits insufficient</b>
                        <br>
                        Student needs to recharge their wallet to cover this class.
                    </div>
                </v-alert>
            </div>
            <div v-else-if="classData.status !== 'expired' && classData.payment_status === 'credits-not-enough'" class="mx-4">
                <router-link v-if="isStudent" :to="{name: 'wallet'}" style="text-decoration: none">
                    <v-alert dense small outlined text type="error" class="e-link px-3" v-ripple>
                        <div class="details-text font-weight-medium">
                            <b>Balance Not Enough:</b>
                            <br>
                            Please recharge as soon as possible..
                            <br>
                            <v-btn small class="mt-2" color="deep-purple" dark depressed style="text-transform: none" :to="{name: 'wallet', query: {borrow: '1'}}" @mousedown.stop @touchstart.stop @click.stop>
                                <v-icon left size="17">mdi-wallet-plus-outline</v-icon>
                                Or Borrow Balance
                            </v-btn>
                        </div>
                    </v-alert>
                </router-link>
                <v-alert v-else outlined dense small text type="error" color="orange darken-2" class="px-3">
                    <div class="details-text font-weight-medium">
                        <b>Student's balance is not enough</b>
                        <br>
                        Student cannot join class as his wallet is not charged.
                    </div>
                </v-alert>
            </div>

            <v-list two-line class="pa-0">

                <v-list-item v-if="classData.has_class_report">
                    <v-list-item-avatar>
                        <v-icon size="20" dark class="primary">mdi-star-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title class="d-flex" style="line-height: normal!important;">
                            <v-rating :value="classData.class_report_rating" color="primary" half-increments background-color="muted lighten-2" hover length="5" readonly dense size="16" half-icon="mdi-star-half-full" ></v-rating>
                            <span class="details-text ms-1">({{classData.class_report_rating}})</span>
                        </v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Class Report</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="isTeacher">
                    <v-list-item-avatar>
                        <v-icon size="20" dark class="blue-grey darken-2">mdi-account</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title style="line-height: normal!important;">{{classData.enrollment.student.name}}</v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Student</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-else-if="!isSession">
                    <v-list-item-avatar>
                        <v-icon size="20" dark class="blue-grey darken-2">mdi-account-tie-voice</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title style="line-height: normal!important;">{{classData.enrollment.teacher.name}}</v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Tutor</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="isConsultation && classData.topic">
                    <v-list-item-avatar>
                        <v-icon size="20" dark class="lime darken-3">mdi-comment-question-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title style="line-height: normal!important; white-space: normal;">{{ classData.topic }}</v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Topic</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item>
                    <v-list-item-avatar>
                        <v-icon size="22" dark :class="handleStatus(classData.status).color">mdi-calendar</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="py-2">
                        <v-list-item-title style="line-height: normal!important;">{{classDateFormated}}</v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Class Date</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item>
                    <v-list-item-avatar>
                        <v-icon dark class="success">mdi-clock-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="py-2">
                        <v-list-item-title style="line-height: normal!important;">
                            {{startTime}}
                            <span class="details-text text--text">({{timeAgo}})</span>
                        </v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Start Time</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item>
                    <v-list-item-avatar>
                        <v-icon dark class="red darken-1">mdi-clock-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="py-2">
                        <v-list-item-title style="line-height: normal!important;">{{endTime}}</v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">End Time</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="isTeacher && classData.teacher_joined_at">
                    <v-list-item-avatar>
                        <v-icon dark class="success">mdi-account-check</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="py-2">
                        <v-list-item-title style="line-height: normal!important;">
                            You joined at {{formatTime(classData.teacher_joined_at, true)}}
                        </v-list-item-title>
                        <v-list-item-subtitle style="line-height: normal!important;">Joined</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                <v-alert v-if="isTeacher && !classData.can_join && !classData.teacher_joined_at && classData.status === 'scheduled'" dense small text color="blue darken-1" class="px-3 mx-3 mb-0">
                    <div class="details-text font-weight-medium strong-text--text">
                        <v-icon color="blue darken-2" size="18" class="me-1">mdi-information-outline</v-icon>
                        Zoom link will be available 15 minutes before class starts
                    </div>
                </v-alert>

                <v-expand-transition>

                    <div v-if="showMoreInfo">
                        <v-divider inset class="my-1"></v-divider>
                        <v-list-item v-if="classData.student_age">
                            <v-list-item-avatar>
                                <v-icon dark class="indigo">mdi-calendar-account</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content class="py-2">
                                <v-list-item-title style="line-height: normal!important;">{{classData.student_age}} years old</v-list-item-title>
                                <v-list-item-subtitle style="line-height: normal!important;">Student Age</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="classData.student_timezone">
                            <v-list-item-avatar>
                                <v-icon dark class="indigo">mdi-map-clock</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content class="py-2">
                                <v-list-item-title style="line-height: normal!important;"><timezone-clock :timezone="classData.student_timezone" /></v-list-item-title>
                                <v-list-item-subtitle style="line-height: normal!important;">Student Time ({{classData.student_timezone}})</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                    </div>
                </v-expand-transition>

                <v-list-item v-if="classData.student_age || classData.student_timezone" @click="showMoreInfo = !showMoreInfo" dense>
                    <v-list-item-icon style="margin-top: 10px; margin-bottom: 10px;">
                        <v-icon color="text">{{showMoreInfo ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <span v-if="!showMoreInfo">Show More</span>
                        <span v-else>Show Less</span>
                    </v-list-item-content>
                </v-list-item>

                <v-divider inset></v-divider>
            </v-list>
        </v-card-text>
        <v-card-actions class="pa-4 pt-2" :style="`${iosSafeArea ? 'padding-bottom: 20px!important; ' : ''}${capRunning ? 'padding-bottom: 60px!important; ' : ''}`">
            <template v-if="classData.holiday_on_hold">
                <!-- No actions available for on-hold classes -->
            </template>
            <template v-else-if="classData.editable && !canJoinNow && !classData.uncharged">
                <div class="w-100">
                    <template v-if="classData.waiting_cancelation_request">
                        <v-alert v-if="isMyCancelationRequest" dense small type="warning" text class="px-3 mb-0">
                            <div class="details-text font-weight-medium">
                                Your cancellation request is waiting for the {{ isTeacher ? 'student' : 'tutor' }} to approve.
                            </div>
                        </v-alert>
                        <template v-else>
                            <v-alert dense small type="warning" text color="orange darken-2" class="px-3 mb-2">
                                <div class="details-text font-weight-medium">
                                    {{ isTeacher ? 'Student' : 'Tutor' }} requested to cancel this class — waiting for your approval.
                                </div>
                            </v-alert>
                            <v-row>
                                <v-col cols="6" class="pe-1 py-0">
                                    <v-btn color="teal" disabled depressed large block>
                                        <v-icon left>mdi-calendar-clock-outline</v-icon>
                                        reschedule
                                    </v-btn>
                                </v-col>
                                <v-col cols="6" class="ps-1 py-0">
                                    <v-btn color="red darken-1" disabled depressed large block>
                                        <v-icon left>mdi-close-box-outline</v-icon>
                                        Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>
                    </template>
                    <v-row v-else>
                        <v-col cols="6" class="pe-1 py-0">
                            <v-btn color="teal" :loading="rescheduling" :disabled="!!classData.waiting_reschedule_request" depressed large block class="white--text" @click="rescheduleDialog = true; rescheduleDate = classData.date">
                                <v-icon left>mdi-calendar-clock-outline</v-icon>
                                reschedule
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="ps-1 py-0">
                            <v-btn v-if="classData.can_cancel_directly" :disabled="classData.canceled" :loading="canceling" color="red darken-1" depressed large block class="white--text" @click="cancelMode = 'direct'; cancelDialog = true">
                                <v-icon left>mdi-close-box-outline</v-icon>
                                Cancel
                            </v-btn>
                            <v-btn v-else-if="classData.can_request_cancel" :disabled="!!classData.waiting_reschedule_request" :loading="canceling" color="orange darken-2" depressed large block class="white--text" style="min-width: 0; padding: 0 8px;" @click="cancelMode = 'request'; cancelDialog = true">
                                <v-icon left small class="me-1">mdi-close-box-multiple-outline</v-icon>
                                <span class="text-truncate">Request Cancel</span>
                            </v-btn>
                            <v-btn v-else disabled depressed large block style="min-width: 0; padding: 0 8px;">
                                <span class="text-truncate">Can't cancel</span>
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>
            </template>
            <div v-else-if="isStudent && !classData.holiday_on_hold && !classData.canceled && !classData.absent && classData.id && classData.status !== 'expired'" class="w-100">
                
                <v-alert v-if="!classData.editable && !classData.joinable && !isSession" dense small type="info" text color="grey" class="px-3">
                    <div class="details-text font-weight-medium strong-text--text">
                        You can't cancel or reschedule because less than 30 minutes left until class starts
                    </div>
                </v-alert>
                <v-alert v-if="!classData.joinable && !isSession" dense small outlined text type="info" class="px-3" v-ripple>
                    <div class="details-text font-weight-medium strong-text--text">
                        <b>Wait for Teacher:</b>
                        <br>
                        The teacher hasn't joined the classroom yet, please wait..
                    </div>
                </v-alert>

                <v-alert v-if="isSession && !classData.meeting_link" dense small outlined text type="info" class="px-3" v-ripple>
                    <div class="details-text font-weight-medium strong-text--text">
                        <b>Wait for Meeting Link:</b>
                        <br>
                        We will send the meeting link to your email soon. Please check back later.
                    </div>
                </v-alert>
                <v-alert v-if="isSession && isEvalEnded" dense small outlined text type="warning" class="px-3" v-ripple>
                    <div class="details-text font-weight-medium strong-text--text">
                        <b>Session Ended:</b>
                        <br>
                        This {{ sessionLabel.toLowerCase() }} session has ended and cannot be joined.
                    </div>
                </v-alert>
                <v-alert v-if="isSession && classData.meeting_link && !isEvalReady && !isEvalEnded" dense small outlined text type="info" class="px-3" v-ripple>
                    <div class="details-text font-weight-medium strong-text--text">
                        <b>Session hasn't started yet:</b>
                        <br>
                        You can join the {{ sessionLabel.toLowerCase() }} session 15 minutes before it starts.
                    </div>
                </v-alert>

                <v-btn v-if="isSession" :color="sessionColor" depressed :loading="joiningEval" :disabled="!isEvalReady" large block class="white--text" @click.prevent="joinEvaluation(classData.id)">
                    <v-icon left class="me-3">mdi-motion-play-outline</v-icon>
                    Join {{ sessionLabel }}
                </v-btn>
                <v-btn v-else color="blue darken-3" depressed :loading="joining" :disabled="!classData.joinable || classData.status === 'expired'" large block class="white--text" @click.prevent="joinClass(classData.id)">
                    <v-icon left class="me-3">mdi-motion-play-outline</v-icon>
                    Join Class
                </v-btn>
                <transition name="scroll-y-transition" mode="out-in">
                    <v-btn type="a" v-if="temporarilyJoinUrl" :target="capRunning ? '_system' : '_blank'" :href="temporarilyJoinUrl" small block text class="mt-2 grey lighten-4" style="text-transform: none">Not Working? Click here to join...</v-btn>
                </transition>
            </div>

            <template v-if="isTeacher && !classData.holiday_on_hold && !classData.canceled && !classData.absent">
                <div v-if="canEditJoinUrl || classData.can_join || (classData.teacher_joined_at && classData.can_mark_attended && classData.status !== 'scheduled' && !classData.attended && !classData.is_unchargeable)" class="w-100 mt-2">
                    <template v-if="classData.can_join || (classData.teacher_joined_at && classData.can_mark_attended && classData.status !== 'scheduled' && !classData.attended && !classData.is_unchargeable)">
                        <v-btn v-if="classData.can_join" :disabled="classData.payment_status === 'credits-not-enough'" color="blue darken-3" depressed :loading="joiningTeacher" large block class="white--text" :class="{'mb-4': classData.teacher_joined_at && classData.can_mark_attended && classData.status !== 'scheduled' && !classData.attended && !classData.is_unchargeable}" @click.prevent="teacherJoinClass(classData.id)">
                            <v-icon left class="me-3">mdi-motion-play-outline</v-icon>
                            Join Class
                        </v-btn>

                        <transition name="scroll-y-transition" mode="out-in">
                            <v-btn type="a" v-if="temporarilyJoinUrl" :target="capRunning ? '_system' : '_blank'" :href="temporarilyJoinUrl" small block text class="mt-n1 grey lighten-4" :class="{'mb-3': classData.teacher_joined_at && classData.can_mark_attended && classData.status !== 'scheduled' && !classData.attended && !classData.is_unchargeable}" style="text-transform: none">Not Working? Click here to join...</v-btn>
                        </transition>

                        <v-row v-if="classData.teacher_joined_at && classData.can_mark_attended && classData.status !== 'scheduled' && !classData.attended && !classData.is_unchargeable" class="mt-0 pt-0">
                            <v-col v-if="!classData.has_class_report && classData.payment_status !== 'credits-not-enough'" cols="12" class="pt-0">
                                <v-btn color="primary" depressed :loading="attendedLoading" large block @click="addReportDialog = true">
                                    <v-icon left class="me-3">mdi-file-document-outline</v-icon>
                                    <span>Add Class Report</span>
                                </v-btn>
                            </v-col>

                            <v-col cols="6" class="pe-1 py-0">
                                <v-btn :disabled="!classData.has_class_report || classData.attended || classData.payment_status === 'credits-not-enough'" color="success" depressed :loading="attendedLoading" large block class="white--text" @click="attendedDialog = true">
                                    <v-icon left class="me-3">mdi-account-check</v-icon>
                                    Attended
                                </v-btn>
                            </v-col>
                            <v-col cols="6" class="ps-1 py-0">
                                <v-btn :disabled="classData.absent || classData.payment_status === 'credits-not-enough'" color="blue-grey" depressed :loading="absentLoading" large block class="white--text" @click="absentDialog = true">
                                    <v-icon left class="me-3">mdi-close-octagon-outline</v-icon>
                                    Absent
                                </v-btn>
                            </v-col>
                        </v-row>
                    </template>

                    <v-btn v-if="canEditJoinUrl" small block text class="mt-2 grey--text text--darken-1" style="text-transform: none" @click="openEditUrlDialog">
                        <v-icon size="16" left class="me-1">{{ classData.join_url ? 'mdi-link-variant-plus' : 'mdi-link-plus' }}</v-icon>
                        {{ classData.join_url ? 'Edit Zoom Link' : 'Add Zoom Link' }}
                    </v-btn>
                </div>
            </template>
        </v-card-actions>

        <v-dialog v-model="absentDialog" max-width="350">
            <v-card>
                <v-card-title>Student Absent Confirmation</v-card-title>
                <v-card-text>
                    Are you sure that student absent? you can't undo this action...
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" @click="absentDialog = false">Cancel</v-btn>
                    <v-btn text color="red" @click="markAsAbsent">Confirm</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="editUrlDialog" max-width="480" :fullscreen="phoneOnly">
            <v-card>
                <v-card-title>Edit Zoom Link</v-card-title>
                <v-card-text>
                    <div class="caption mb-2 grey--text text--darken-1">
                        Paste a Zoom (or other meeting) URL below. Students will be redirected to this link when they click "Join Class".
                    </div>
                    <v-text-field v-model="newJoinUrl" label="Meeting URL" placeholder="https://zoom.us/j/..." outlined dense :error-messages="editUrlError" @input="editUrlError = ''"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" :disabled="editUrlLoading" @click="editUrlDialog = false">Cancel</v-btn>
                    <v-btn color="primary" depressed :loading="editUrlLoading" :disabled="!newJoinUrl" @click="saveJoinUrl">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="attendedDialog" max-width="350">
            <v-card>
                <v-card-title>Attendance confirmation</v-card-title>
                <v-card-text>
                    Are you sure that student attended? after confirmation, student will be charged for this class and it'll be marked as "Attended"
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" @click="attendedDialog = false">Cancel</v-btn>
                    <v-btn text color="success" @click="markAsAttended">Confirm</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="addReportDialog" max-width="720" :fullscreen="phoneOnly" :fluid="phoneOnly">
            <v-card :class="['d-flex flex-column', {'fill-height': phoneOnly}]" :style="`${!phoneOnly ? 'max-height: 90vh;' : ''}${iosSafeArea ? 'padding-top: 45px!important;' : ''}`">
                <v-card-title class="mb-1 flex-shrink-0">Class Report</v-card-title>
                <v-card-subtitle class="flex-shrink-0">fill form inputs and save class report</v-card-subtitle>
                <v-card-text class="flex-grow-1" style="overflow-y: auto;">
                    <v-form ref="addReportForm">
                        <div class="mb-4 d-flex justify-center align-center">
                            <v-rating v-model="reportForm.rating" color="primary" half-increments background-color="muted lighten-2" hover length="5" size="28" half-icon="mdi-star-half-full" ></v-rating>
                            <div class="text-center primary--text font-weight-bold ms-4">
                                <v-chip small :outlined="reportForm.rating < 5" :color="!reportForm.rating || reportForm.rating < 2 ? 'muted' : 'primary'">{{reportForm.rating ? reportForm.rating.toFixed(1) : '---'}}</v-chip>
                            </div>
                        </div>
                        <v-divider class="mb-6 mb-lg-8"></v-divider>
                        <div class="mb-4">
                            <highlighted-textarea
                                ref="classActivityEditor"
                                v-model="reportForm.class_activity"
                                label="Class Activity *"
                                :disabled="savingClassReport"
                                :min-rows="9"
                                hint="Replace each highlighted slot with the actual detail. You can also clear the field and write your own."
                            />
                        </div>
                        <div class="mb-4">
                            <highlighted-textarea
                                ref="homeworkEditor"
                                v-model="reportForm.homework"
                                label="Homework *"
                                :disabled="savingClassReport"
                                :min-rows="7"
                            />
                        </div>
                        <v-textarea v-model="reportForm.notes" label="Teacher Notes" :disabled="savingClassReport" rows="4" outlined></v-textarea>
                    </v-form>
                    <v-alert v-if="missingRequiredFields.length || unfilledPlaceholders.length" dense small type="warning" class="mt-3 mb-0">
                        <div v-if="missingRequiredFields.length" class="medium-text font-weight-medium">
                            Required: {{ missingRequiredFields.join(', ') }}
                        </div>
                        <div v-if="unfilledPlaceholders.length" :class="{'mt-2': missingRequiredFields.length}">
                            <div class="medium-text font-weight-medium">
                                Replace the highlighted placeholders before saving
                                <span class="text-no-wrap">({{ unfilledPlaceholders.length }} remaining)</span>:
                            </div>
                            <div class="caption mt-1">{{ unfilledPlaceholdersPreview }}</div>
                        </div>
                    </v-alert>
                </v-card-text>
                <v-card-actions class="flex-shrink-0" style="border-top: 1px solid rgba(0, 0, 0, 0.12);" :style="`${iosSafeArea ? 'padding-bottom: 20px!important;' : ''}${capRunning ? 'padding-bottom: 60px!important;' : ''}`">
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" :disabled="savingClassReport" @click="addReportDialog = false">Cancel</v-btn>
                    <v-btn text color="primary" :disabled="!canSaveReport" @click="saveClassReport">Save Report</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="cancelDialog" max-width="400">
            <v-card>
                <v-card-title>{{ cancelMode === 'request' ? 'Cancellation Request' : 'Cancel Confirmation' }}</v-card-title>
                <v-card-text>
                    <v-alert v-if="classData.available_class_changes !== null && classData.available_class_changes !== undefined" dense small type="warning" icon="mdi-progress-alert" text :color="classData.available_class_changes === 0 ? 'red' : 'orange'" class="px-3 mb-3">
                        <div class="deep-orange--text text--darken-1 medium-text">
                            Remaining <b>{{classData.available_class_changes}} times</b> from {{classData.monthly_class_changes}} to cancel or reschedule this month
                        </div>
                        <div v-if="!isTeacher" class="caption mt-1 grey--text text--darken-1">
                            Note: The uncharged classes count toward this limit
                        </div>
                    </v-alert>

                    <div v-if="classData.limit_exceeded" class="mb-3">
                        <v-alert dense small type="error" icon="mdi-alert-circle" class="px-3 mb-0">
                            <div class="medium-text">
                                You've exceeded the monthly limit for cancelling or rescheduling classes.
                            </div>
                        </v-alert>
                    </div>
                    <div v-else-if="cancelMode === 'request'">
                        Less than 3 hours left until class starts. The {{ isTeacher ? 'student' : 'tutor' }} must approve this cancellation. They will be notified immediately.
                    </div>
                    <div v-else>
                        <div v-if="isTeacher">
                            Are you sure that you want to cancel this class?
                        </div>
                        <div v-else>
                            if you canceled the class, You will not be charged and you'll not be able to schedule another class later.
                        </div>
                    </div>

                    <div class="mt-5">
                        <v-textarea v-model="cancelationReason" outlined placeholder="Reason of cancelation *" color="error darken-1" hide-details no-resize class="medium-text"></v-textarea>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" @click="cancelDialog = false">Cancel</v-btn>
                    <v-btn text :color="cancelMode === 'request' ? 'orange darken-2' : 'red'" :disabled="classData.limit_exceeded || !cancelationReason.trim()" @click="cancelClass">{{ cancelMode === 'request' ? 'Send Request' : 'Confirm' }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rescheduleDialog" max-width="400">
            <v-card>
                <v-card-title class="mb-2">Reschedule Class</v-card-title>
                <v-card-text>
                    <v-alert v-if="availableReschedules !== null && availableReschedules !== undefined" dense small type="warning" icon="mdi-progress-alert" text :color="availableReschedules === 0 ? 'red' : 'orange'" class="px-3">
                        <div class="deep-orange--text text--darken-1 medium-text">
                            Remaining <b>{{availableReschedules}} times</b> from {{monthlyRescheduleLimit}} to {{ hasRescheduleLimit ? 'reschedule' : 'cancel or reschedule' }} this month
                        </div>
                        <div v-if="!isTeacher" class="caption mt-1 grey--text text--darken-1">
                            Note: The uncharged classes count toward this limit
                        </div>
                    </v-alert>
                    <div v-if="rescheduleLimitExceeded" class="mb-3">
                        <v-alert dense small type="error" icon="mdi-alert-circle" class="px-3 mb-0">
                            <div class="medium-text">
                                You've exceeded the monthly limit for {{ hasRescheduleLimit ? 'rescheduling' : 'cancelling or rescheduling' }} classes.
                            </div>
                        </v-alert>
                    </div>
                    <v-form v-if="!rescheduleLimitExceeded">
                         <v-menu ref="menu" v-model="rescheduleDateMenu" :close-on-content-click="false" :return-value.sync="rescheduleDate" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model="rescheduleDate" placeholder="Class Date" color="teal" label="Class Date" outlined hide-details prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
                            </template>
                            <v-date-picker color="teal" v-model="rescheduleDate" :min="minRescheduleDate" no-title scrollable>
                                <v-spacer></v-spacer>
                                <v-btn text color="muted" @click="rescheduleDateMenu = false">
                                    Cancel
                                </v-btn>
                                <v-btn text color="teal" :disabled="rescheduleLimitExceeded" @click="$refs.menu.save(rescheduleDate)">
                                    Confirm
                                </v-btn>
                            </v-date-picker>
                        </v-menu>

                        <div class="mt-4">
                            <time-input v-model="rescheduleTime" half-format outlined label="Start Time" placeholder="Start Time" color="teal" />
                        </div>

                        <div class="mt-4">
                            <v-switch v-model="permanentReschedule" label="Permanent Reschedule" color="teal" hide-details ></v-switch>
                        </div>

                        <div class="mt-5">
                            <v-textarea v-model="rescheduleReason" outlined placeholder="Reason of reschedule" color="teal darken-1" hide-details no-resize class="medium-text"></v-textarea>
                        </div>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="muted" @click="rescheduleDialog = false">Cancel</v-btn>
                    <v-btn text color="teal" :disabled="rescheduleLimitExceeded" @click="rescheduleClass">Reschedule</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

import TimeInput from '@/components/custom/time-input'
import TimezoneClock from '@/components/default/timezone-clock'
import HighlightedTextarea from '@/components/custom/highlighted-textarea'

import { CLASS_ACTIVITY_TEMPLATE, HOMEWORK_TEMPLATE, PLACEHOLDER_REGEX } from '@/helpers/class-report-templates'


export default {
    components: {TimeInput, TimezoneClock, HighlightedTextarea},
    data(){
        const min = new Date();
        min.setDate(min.getDate()+1);

        return {
            showMoreInfo: false,
            joining: false,
            joiningEval: false,
            temporarilyJoinUrl: '',
            joiningTeacher: false,

            absentDialog: false,
            absentLoading: false,

            editUrlDialog: false,
            editUrlLoading: false,
            newJoinUrl: '',
            editUrlError: '',
            
            attendedDialog: false,
            attendedLoading: false,

            cancelDialog: false,
            canceling: false,
            cancelationReason: '',
            cancelMode: 'direct',

            addReportDialog: false,
            savingClassReport: false,
            reportForm: {
                rating: '',
                class_activity: '',
                homework: '',
                notes: '',
            },

            rescheduleDialog: false,
            rescheduling: false,
            rescheduleDateMenu: false,
            rescheduleDate: '' ,
            rescheduleTime: '' ,
            permanentReschedule: false,
            rescheduleReason: '',

            minRescheduleDate: min.toISOString().substr(0, 10),

            intervalid: null,
            timeAgo: '',
            timeNow: '',
            countdownTime: '',
            startsInSeconds: null,
            endsInSeconds: null,
        }
    },
    props: {
        classData: {},
        reloading: {type: Boolean, default: false}
    },
    computed: {
        isMyCancelationRequest(){
            const req = this.classData && this.classData.waiting_cancelation_request;
            if (!req || !this.user) return false;
            return req.sender_id === this.user.id;
        },
        canJoinNow(){
            // True once the join action is available for this user — teacher's
            // `can_join` (zoom link exists, class not blocked) or student's
            // `joinable` (link + sufficient credits, etc.). When true, the
            // Reschedule/Cancel block is suppressed even if `editable` would
            // otherwise allow it.
            if (this.isTeacher) return !!this.classData.can_join;
            if (this.isStudent) return !!this.classData.joinable;
            return false;
        },
        startTime(){
            // const d = new Date(this.classData.start);
            // const timeString = d.toLocaleTimeString();
            // const times = timeString.split(' ')[0].split(':');
            // return `${times[0]}:${times[1]} ${timeString.split(' ')[1]}`

            const t = new Date(this.classData.start);
            return this.formatTime(t);
        },
        endTime(){
            const t = new Date(this.classData.end);
            return this.formatTime(t);
        },
        classDateFormated(){
            const t = new Date(this.classData.start)
            const options = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
            return t.toLocaleDateString("en-US", options);
        },
        isSoon(){
            if (this.startsInSeconds === null || this.startsInSeconds <= 0){
                return false;
            }
            const hours = this.startsInSeconds / 3600;
            return hours < 12;
        },
        isEvalEnded(){
            if (!this.isSession || !this.classData.end) return false;
            return this.endsInSeconds !== null && this.endsInSeconds <= 0;
        },
        isEvalReady(){
            if (!this.isSession || !this.classData.meeting_link) return false;
            if (this.isEvalEnded) return false;
            // Ready if within 15 minutes of start (900 seconds)
            return this.startsInSeconds !== null && this.startsInSeconds <= 900;
        },
        isConsultation(){
            return !!this.classData.is_consultation;
        },
        isSession(){
            return !!this.classData.is_evaluation || !!this.classData.is_consultation;
        },
        sessionLabel(){
            return this.isConsultation ? 'Consultation' : 'Evaluation';
        },
        sessionColor(){
            return this.isConsultation ? 'lime darken-3' : 'cyan darken-1';
        },
        canEditJoinUrl(){
            if (!this.isTeacher) return false;
            if (this.appData && this.appData.teacher_can_edit_zoom_link === false) return false;
            if (this.classData.canceled || this.classData.absent || this.classData.holiday_on_hold) return false;
            if (this.classData.status === 'expired') return false;
            // Only show within 15 minutes of class start (or after it has started)
            return this.startsInSeconds !== null && this.startsInSeconds <= 900;
        },
        unfilledPlaceholders(){
            const found = new Set();
            const scan = s => {
                if (!s) return;
                const matches = String(s).match(PLACEHOLDER_REGEX) || [];
                matches.forEach(m => found.add(m));
            };
            scan(this.reportForm.class_activity);
            scan(this.reportForm.homework);
            return [...found];
        },
        unfilledPlaceholdersPreview(){
            const list = this.unfilledPlaceholders;
            if (list.length <= 4) return list.join('  ·  ');
            return list.slice(0, 4).join('  ·  ') + `  · +${list.length - 4} more`;
        },
        missingRequiredFields(){
            const missing = [];
            if (!this.reportForm.class_activity || !String(this.reportForm.class_activity).trim()) missing.push('Class Activity');
            if (!this.reportForm.homework || !String(this.reportForm.homework).trim()) missing.push('Homework');
            return missing;
        },
        canSaveReport(){
            return !this.savingClassReport
                && this.unfilledPlaceholders.length === 0
                && this.missingRequiredFields.length === 0;
        },
        rescheduleLimitExceeded(){
            const v = this.classData.reschedule_limit_exceeded;
            return v !== null && v !== undefined ? v : this.classData.limit_exceeded;
        },
        availableReschedules(){
            const v = this.classData.available_reschedules;
            return v !== null && v !== undefined ? v : this.classData.available_class_changes;
        },
        monthlyRescheduleLimit(){
            const v = this.classData.monthly_reschedule_limit;
            return v !== null && v !== undefined ? v : this.classData.monthly_class_changes;
        },
        hasRescheduleLimit(){
            return this.classData.monthly_reschedule_limit !== null && this.classData.monthly_reschedule_limit !== undefined;
        },
        ...GlobalComputed
    },
    methods: {
        joinEvaluation(id){
            // Use evaluation_id (numeric) if available, otherwise strip 'eval-' prefix
            const evalId = this.classData.evaluation_id || String(id).replace('eval-', '');
            if (!this.joiningEval){
                this.joiningEval = true;
                window.axios.post(`/evaluations/${evalId}/join`).then(response => {
                    this.joiningEval = false;
                    const url = response.data.meeting_link;
                    window.setTimeout(()=>{
                        this.openLink(url);
                        this.setTemporarilyJoinUrl(url);
                    }, 300);
                }).catch(e => {
                    this.joiningEval = false;
                    this.showMsg('Error occurred during joining, please contact support', 'error');
                });
            }
        },
        joinClass(id){
            if (!this.joining){
                this.joining = true;
                this.$store.dispatch('joinClass', {id}).then(response=>{
                    this.joining = false;

                    const url = response.data.join_url;
                    window.setTimeout(()=>{
                        // window.open(url, '_system');
                        this.openLink(url);
                        this.setTemporarilyJoinUrl(url);
                    }, 300);

                    this.showClassDialog = false;

                }).catch(e=>{
                    this.joining = false;
                    this.showMsg('Error occurred during joining, please contact support', 'error');
                })
            }
        },
        setTemporarilyJoinUrl(url){
            this.temporarilyJoinUrl = url;
            window.setTimeout(()=>{
                this.temporarilyJoinUrl = '';
            }, 4000);
        },
        formatTime(t, isTimestamp=false){
            if (isTimestamp){
                t = new Date(t);
            }
            let hours = t.getHours(), 
                minutes = t.getMinutes() + '', 
                ampm = 'AM';

            if(hours > 11){
                hours = hours - 12;
                ampm = 'PM';
            }

            if(hours == 0) hours = 12;
            if(minutes.length == 1) minutes = '0' + minutes;

            return `${hours}:${minutes} ${ampm}`;
        },
        openLink(url){
            let a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";
            a.href = url;
            a.setAttribute('target', '_system');
            a.click();
            document.body.removeChild(a);
        },
        teacherJoinClass(id){
            if (!this.joiningTeacher){
                this.joiningTeacher = true;
                this.$store.dispatch('teacherJoinClass', {id}).then(response=>{
                    this.joiningTeacher = false;
                    const url = response.data.join_url;
                    if (url) {
                        window.setTimeout(()=>{
                            this.openLink(url);
                            this.setTemporarilyJoinUrl(url);
                        }, 300);
                    }
                }).catch(e=>{
                    this.joiningTeacher = false;
                    this.showMsg('Error occurred during joining, please contact support', 'error');
                });
            }
        },
        openEditUrlDialog(){
            this.newJoinUrl = this.classData.join_url || '';
            this.editUrlError = '';
            this.editUrlDialog = true;
        },
        saveJoinUrl(){
            const url = (this.newJoinUrl || '').trim();
            if (!url){
                this.editUrlError = 'URL is required';
                return;
            }
            try {
                new URL(url);
            } catch (e) {
                this.editUrlError = 'Please enter a valid URL';
                return;
            }
            if (this.editUrlLoading) return;

            this.editUrlLoading = true;
            this.$store.dispatch('updateClassJoinUrl', {id: this.classData.id, join_url: url}).then(()=>{
                this.editUrlLoading = false;
                this.editUrlDialog = false;
                this.classData.join_url = url;
                this.classData.can_join = !this.classData.canceled && !this.classData.absent && !this.classData.holiday_on_hold && this.classData.status !== 'expired';
                this.showMsg('Zoom link updated', 'success');
            }).catch(e=>{
                this.editUrlLoading = false;
                const msg = e && e.response && e.response.data && e.response.data.message
                    ? e.response.data.message
                    : 'Failed to update link, please try again';
                this.editUrlError = msg;
            });
        },
        markAsAbsent(){
            const id = this.classData.id;
            if (!this.absentLoading){
                this.absentDialog = false;
                this.absentLoading = true;
                this.$store.dispatch('markAsAbsent', {id}).then(response=>{
                    this.absentLoading = false;
                    
                    // Update the local classData to reflect the new state
                    this.classData.absent = true;
                    this.classData.full_status = 'absent';
                    
                    this.showMsg('Class marked as absent successfully', 'success');
                    
                    // Emit event to parent to refresh the class list if needed
                    this.$emit('refresh');
                }).catch(e=>{
                    this.absentLoading = false;
                    const apiMsg = e && e.response && e.response.data && e.response.data.message;
                    this.showMsg(apiMsg || 'Error occurred during preforming your action', 'error');
                })
            }
        },
        markAsAttended(){
            const id = this.classData.id;
            if (!this.attendedLoading){
                this.attendedDialog = false;
                this.attendedLoading = true;
                this.$store.dispatch('markAsAttended', {id}).then(response=>{
                    this.attendedLoading = false;
                    
                    // Update the local classData to reflect the new state
                    this.classData.attended = true;
                    this.classData.payment_status = 'paid';
                    // this.classData.full_status = 'attended';
                    
                    this.showMsg('Class marked as attended successfully', 'success');
                    
                    // Emit event to parent to refresh the class list if needed
                    this.$emit('refresh');
                }).catch(e=>{
                    this.attendedLoading = false;
                    this.showMsg('Error occurred during preforming your action', 'error');
                })
            }
        },
        saveClassReport(){
            const id = this.classData.id;
            if (!this.savingClassReport){
                this.savingClassReport = true;
                const data = this.reportForm;
                this.$store.dispatch('saveClassReport', {id, data}).then(response=>{
                    this.savingClassReport = false;
                    this.addReportDialog = false;
                    this.showMsg('Class Report Saved Successfully', 'success');
                    
                    // Update the local classData to reflect the new state
                    this.classData.has_class_report = true;
                    this.classData.class_report_rating = this.reportForm.rating;
                    
                    // Reset the form
                    this.reportForm = {
                        rating: '',
                        class_activity: '',
                        homework: '',
                        notes: '',
                    };
                }).catch(e=>{
                    this.savingClassReport = false;
                    // show response error message
                    if (e && e.response && e.response.data && e.response.data.message){
                        this.showMsg(e.response.data.message, 'error');
                    }else{
                        this.showMsg('Error occurred during preforming your action', 'error');
                    }
                })
            }
        },
        cancelClass(){
            const id = this.classData.id;
            this.cancelDialog = false;
            if (!this.canceling){
                this.canceling = true;
                const data = {
                    cancelation_reason: this.cancelationReason
                };

                this.$store.dispatch('cancelClass', {id, data}).then(response=>{
                    this.canceling = false;
                    const mode = response && response.data ? response.data.mode : null;
                    if (mode === 'requested'){
                        this.showMsg('Cancellation request sent', 'success');
                    } else if (mode === 'canceled'){
                        this.showMsg('Class canceled', 'success');
                    }
                    this.cancelationReason = '';
                }).catch(e=>{
                    this.canceling = false;
                    if (e && e.response && e.response.data === 'LIMIT_EXCEEDED'){
                        this.showMsg('Monthly limit exceeded', 'error');
                    }else if (e && e.response && e.response.data && e.response.data.message){
                        this.showMsg(e.response.data.message, 'error');
                    }else{
                        this.showMsg('Error occurred during preforming your action', 'error');
                    }
                })
            }
        },
        rescheduleClass(){
            if (this.rescheduleTime && this.rescheduleDate){
                const id = this.classData.id;
                this.rescheduleDialog = false;
                if (!this.rescheduling){
                    this.rescheduling = true;
                    this.$store.dispatch('rescheduleClass', {id, date: this.rescheduleDate, time: this.rescheduleTime, permanent: this.permanentReschedule, reason: this.rescheduleReason}).then(response=>{
                        this.rescheduling = false;
                        const mode = response && response.data && response.data.mode;
                        if (mode === 'rescheduled'){
                            this.showMsg('Class rescheduled successfully', 'success');
                        }else{
                            this.showMsg('Reschedule request submitted', 'success');
                        }
                    }).catch(e=>{
                        this.rescheduling = false;
                        
                        if (e && e.response && e.response.data === 'LIMIT_EXCEEDED'){
                            this.showMsg('Monthly limit exceeded', 'error');
                        }else if (e && e.response && e.response.data && e.response.data.message){
                            this.showMsg(e.response.data.message, 'error');
                        }else{
                            this.showMsg('Error occurred during preforming your action', 'error');
                        }
                    })
                }
            }else{
                this.showMsg('Fill required inputs', 'error');
            }
        },
        setTimeIntervals(time) {
            const self = this;
            self.setDowntimer(time);
            self.timeAgo = self.timeSince(time, true);

            // update time every 1s
            this.intervalid = setInterval(function () {
                self.timeAgo = self.timeSince(time, true);
                self.setDowntimer(time);
            }, 1000);
        },
        timeNowHandler(){
            var date = new Date();
            var h = date.getHours(); // 0 - 23
            var m = date.getMinutes(); // 0 - 59
            var s = date.getSeconds(); // 0 - 59
            var session = "AM";
            
            if(h == 0){
                h = 12;
            }
            
            if(h >= 12){
                session = "PM";
                if (h > 12){
                    h = h - 12;
                }
            }
            
            h = (h < 10) ? "0" + h : h;
            m = (m < 10) ? "0" + m : m;
            s = (s < 10) ? "0" + s : s;
            
            this.timeNow = h + ":" + m + ":" + s + " " + session;
            
            window.setTimeout(this.timeNowHandler, 1000);
        },
        setDowntimer(time){
            let now = + new Date(), // + new Date() = date.valueOf() | it is a timestamp
                start = new Date(time),
                difference = start.valueOf() - now;
                
            this.startsInSeconds = Math.floor(difference / 1000);
            
            if (this.classData && this.classData.end) {
                let end = new Date(this.classData.end);
                this.endsInSeconds = Math.floor((end.valueOf() - now) / 1000);
            }

            if (difference > 0){
                let days = Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds = Math.floor((difference % (1000 * 60)) / 1000);
    
                // this.countdownTime = `${days ? days + ' ' : ''}${hours<10 ? '0' + hours : hours}:${minutes<10 ? '0'+minutes : minutes}:${seconds<10 ? '0' + seconds : seconds}`
                
                let countdownTime = '';
                if (days) countdownTime += `${days} days, `;
                if (hours) countdownTime += `${hours} hours and `;
                if (minutes){
                    if (hours){
                        countdownTime += `${minutes} minutes`
                    }else{
                        countdownTime += `${minutes} minutes and `
                    }
                }
                if (!days && !hours){
                    if (seconds >= 0) countdownTime += `${seconds} seconds`;
                }
                this.countdownTime = countdownTime;
            }
        },
        ...GlobalMethods
    },
    watch: {
        classData(){
            if (this.intervalid){
                window.clearInterval(this.intervalid);
            }
            this.setTimeIntervals(this.classData.start);
        },
        addReportDialog(open){
            if (!open) return;
            if (this.classData.has_class_report) return;
            this.$nextTick(() => {
                const ca = this.$refs.classActivityEditor;
                const hw = this.$refs.homeworkEditor;
                if (!ca || !hw) return;
                if (!this.reportForm.class_activity && !this.reportForm.homework){
                    ca.setRichTemplate(CLASS_ACTIVITY_TEMPLATE);
                    hw.setRichTemplate(HOMEWORK_TEMPLATE);
                } else {
                    ca.setPlainValue(this.reportForm.class_activity);
                    hw.setPlainValue(this.reportForm.homework);
                }
            });
        }
    },
    mounted(){
        this.setTimeIntervals(this.classData.start);
        this.timeNowHandler();
    },
    beforeDestroy(){
        if (this.intervalid){
            window.clearInterval(this.intervalid);
        }
    }
};
</script>